import * as React from 'react';
import SvgCompass from './Compass';

type CompassConstructor = new (canvas: HTMLCanvasElement, heading: HTMLElement) => { stop?: () => void };

// Classic scripts, published by `copy-webpack-plugin` to /dist/marine-compass (see the webpack
// configs). Not bundled on purpose: gl-matrix is UMD and would hand its matrices to `module.exports`
// instead of `window`, and the compass reads the globals
const SCRIPTS = [
  '/dist/marine-compass/gl-matrix.min.js',
  '/dist/marine-compass/fulltilt.min.js',
  '/dist/marine-compass/compass.js',
];

let loading: Promise<CompassConstructor> | null = null;

const loadCompass = (): Promise<CompassConstructor> => {
  if (!loading) {
    loading = SCRIPTS.reduce(
      (chain, src) => chain.then(() => new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[data-marine-compass="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = false;
        script.dataset.marineCompass = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`could not load ${src}`));
        document.head.appendChild(script);
      })),
      Promise.resolve(),
    ).then(() => {
      const Compass = (window as any).Compass;
      if (typeof Compass !== 'function') {
        throw new Error('the marine compass scripts did not define window.Compass');
      }
      return Compass as CompassConstructor;
    });
  }

  return loading;
};

interface MarineCompassProps {
  /** Canvas size in px: the dial fills most of it */
  size?: number;
}

export default function MarineCompass({ size = 189 }: MarineCompassProps) {
  const host = React.useRef<HTMLDivElement | null>(null);
  const [unavailable, setUnavailable] = React.useState(false);

  React.useEffect(() => {
    // Falls back to the SVG dial rather than leaving an empty square
    if (!('WebGLRenderingContext' in window)) {
      setUnavailable(true);
      return;
    }

    const container = host.current;
    let cancelled = false;
    let compass: { stop?: () => void } | null = null;

    loadCompass().then((Compass) => {
      if (cancelled || !container) {
        return;
      }

      // No inline width/height: the attributes size it, and CSS can then shrink it on a narrow screen
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      container.appendChild(canvas);

      // The repo's compass writes its heading into an element; ours stays off screen
      compass = new Compass(canvas, document.createElement('div'));
    }).catch((error) => {
      console.warn('TreeWallet: the marine compass could not start', error);
      if (!cancelled) {
        setUnavailable(true);
      }
    });

    return () => {
      cancelled = true;
      compass?.stop?.();
      if (container) {
        container.replaceChildren();
      }
    };
  }, [size]);

  if (unavailable) {
    return <SvgCompass size={size} />;
  }

  return <div className={'marine-compass'} ref={host} style={{ width: size, height: size }} />;
}
